import { useMemo } from "react";
import { useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import { DataTable } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { RecipeUsageLine } from "@/lib/api/requests/recipes";
import { getRecipeUsageColumns } from "./usage-columns";

interface RecipeUsageListingProps {
  lines: RecipeUsageLine[] | undefined;
  isLoading: boolean;
  /** A failed load is said as such, not shown as "nothing uses this". */
  isError?: boolean;
  onRetry?: () => void;
  emptyMessage: string;
}

/** The recipe lines that use something; a row opens its recipe. */
export function RecipeUsageListing({ lines, isLoading, isError = false, onRetry, emptyMessage }: RecipeUsageListingProps) {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const columns = useMemo(() => getRecipeUsageColumns(i18n), [i18n]);

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (isError) {
    return (
      <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-(--color-danger-bg) px-4 py-3 text-sm text-(--color-danger-text)">
        <span>{t`The recipes using this couldn't be loaded.`}</span>
        {onRetry && (
          <Button type="button" size="sm" variant="outline" onClick={onRetry}>
            {t`Try again`}
          </Button>
        )}
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={lines ?? []}
      emptyMessage={emptyMessage}
      onRowClick={(line) => void navigate({ to: "/recipes/$recipeId", params: { recipeId: String(line.recipeId) } })}
    />
  );
}
